define('fukit/helpers/formatter',['require','exports','module','handlebars','underscore','moment','backbone.radio','appkit/appkit'],function(require, exports, module) {
  //prettier-ignore
  var Handlebars  = require('handlebars'),
      _           = require('underscore'),
      Moment      = require('moment'),
      Radio       = require('backbone.radio'),
      KeyPrefixer = require('appkit/appkit').util.KeyPrefixer;

  function isResourceValue(value) {
    return KeyPrefixer.hasPrefix(value, '@resources.');
  }

  function getResourceValue(value) {
    var resourceName = KeyPrefixer.removePrefix(value, '@resources.');
    return Radio.request('AppKit.resourceService', 'resource', resourceName);
  }

  function isStateValue(value) {
    return KeyPrefixer.hasPrefix(value, '@state');
  }

  function getStateValue(value) {
    //prettier-ignore
    var key = KeyPrefixer.removePrefix(value, '@state.'),
        state = Radio.request('AppKit.controllerService', 'state');

    if (!key) {
      return state;
    } else if (state) {
      return state.get(key);
    } else {
      return null;
    }
  }

  var formatter = {
    empty: function(options) {
      if (_.isString(options)) {
        options = _.extend({}, arguments[1], {
          emptyValue: arguments[0],
        });
      }

      options || (options = {});

      options.emptyValue = _.has(options, 'emptyValue') ? options.emptyValue : '--';

      return function(value) {
        if (value == null || String(value).length < 1) {
          if (options.noHtmlFormatting) {
            return options.emptyValue;
          } else {
            return new Handlebars.SafeString(
              '<span class="muted">' + Handlebars.escapeExpression(options.emptyValue) + '</span>'
            );
          }
        }
        return value;
      };
    },

    date: function(options) {
      if (_.isString(options)) {
        // add legacy support
        options = _.extend({}, arguments[2], {
          format: arguments[0],
          emptyValue: arguments[1],
          //convertToInteger does not exist in legacy implementations, so default to false
          convertToInteger: false,
        });
      }

      options || (options = {});

      //prettier-ignore
      var useCalendarDate = (String(options.format).toLowerCase() === 'calendar_date'),
          useCalendarTime = (String(options.format).toLowerCase() === 'calendar_time');

      options.format = options.format || 'L';

      var emptyFormatter = formatter.empty(options);

      return function(value) {
        if (!value) {
          return emptyFormatter.apply(this, arguments);
        }

        if (options.convertToInteger && _.isString(value)) {
          value = parseInt(value);
        }
        if (useCalendarDate) {
          //prettier-ignore
          var now            = Moment(),
              diff           = Moment(value).diff(now.startOf('day'), 'days', true),
              output         = null,
              calendarFormat =
                diff < -6
                  ? 'sameElse'
                  : diff < -1
                    ? 'lastWeek'
                    : diff < 0
                      ? 'lastDay'
                      : diff < 1 ? 'sameDay' : diff < 2 ? 'nextDay' : diff < 7 ? 'nextWeek' : 'sameElse';

          switch (calendarFormat) {
            case 'sameDay':
              output = 'Today';
              break;
            case 'nextDay':
              output = 'Tomorrow';
              break;
            case 'lastDay':
              output = 'Yesterday';
              break;
            case 'lastWeek':
            case 'nextWeek':
              output = Moment(value).from(Moment());
              output = output.slice(0, 1).toUpperCase() + output.slice(1);
              break;
            case 'sameElse':
              output = Moment(value).format('L');
              break;
          }

          return output;
        }
        if (useCalendarTime) {
          var output = Moment(value).calendar();
          if (output.slice(0, 1) == 'l') {
            output = output.slice(0, 1).toUpperCase() + output.slice(1);
          }
          return output;
        }
        return Moment(value).format(options.format);
      };
    },

    serviceMethod: function(serviceName, requestName) {
      var args = _.rest(arguments, 2);

      return function() {
        var resolvedArgs = _.map(args, function(value) {
          if (isResourceValue(value)) {
            return getResourceValue(value);
          } else if (isStateValue(value)) {
            return getStateValue(value);
          }
          return value;
        });
        return Radio.request.apply(Radio, [serviceName, requestName].concat(resolvedArgs).concat(_.toArray(arguments)));
      };
    },

    invert: function() {
      return function(value) {
        return !value;
      };
    },

    prepend: function(str) {
      return function(value) {
        return str + value;
      };
    },

    append: function(str) {
      return function(value) {
        return value + str;
      };
    },

    evaluateStateModeComparison: function(operand, comparator) {
      return function(mode) {
        if (operand === 'equalTo') {
          return mode === comparator;
        } else if (operand === 'notEqualTo') {
          return mode !== comparator;
        }
      };
    },

    createdOnDate: function(options) {
      var dateFormatter = formatter.date(options);

      return function(date) {
        return 'Created on ' + dateFormatter(date);
      };
    },

    chain: function() {
      var formatterArray = [];
      function chainFormatter(value) {
        _.each(
          formatterArray,
          function(formatter) {
            value = formatter.call(this, value);
          },
          this
        );
        return value;
      }
      _.each(formatter, function(value, key) {
        if (key == 'chain') {
          return;
        }
        chainFormatter[key] = function() {
          formatterArray.push(formatter[key].apply(formatter, arguments));
          return chainFormatter;
        };
      });
      chainFormatter.formatter = function(method) {
        if (_.isFunction(method)) {
          formatterArray.push(method);
        }
        return chainFormatter;
      };
      return chainFormatter;
    },

    duration: function(options) {
      options || (options = {});
      //prettier-ignore
      var startDate = options.startDate,
          endDate = options.endDate;

      return function(model) {
        if (!(model instanceof Backbone.Model)) {
          return;
        }
        var diff = Moment(model.get(endDate) || 0).diff(model.get(startDate) || 0);

        var types = [{ type: 'day' }, { type: 'hour' }, { type: 'minute' }];

        return _.chain(types)
          .each(function(context) {
            context.duration = Math.floor(Moment.duration(diff).get(context.type));
          })
          .filter(function(context) {
            return context.duration > 0;
          })
          .map(function(context) {
            var value = context.duration + ' ' + context.type;
            if (context.duration > 1) {
              value += 's';
            }
            return value;
          })
          .value()
          .join(' ');
      };
    },

    handlebarSafeString: function() {
      return function(value) {
        return new Handlebars.SafeString(value);
      };
    },

    toBoolean: function() {
      return function(value) {
        return !!value;
      };
    },

    richTextToSingleLine: function() {
      return function(value) {
        {
          if (!_.isString(value)) {
            value = '';
          }

          // format rich text value into simple one line
          //prettier-ignore
          var breaks = /<br\s?\/>|<\/p>/g,
              tags = /<[^>]+>/g,
              spaces = /&nbsp;/g;

          return _.unescape(
            value
              .replace(spaces, ' ')
              .replace(breaks, ' ')
              .replace(tags, '')
          );
        }
      };
    },
  };

  module.exports = formatter;
});

